import { useMemo, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { useSite } from '@/app/site-context';
import { usePublicCategories, usePublicPost, usePublicPosts } from '@/lib/api/queries';
import { useDocumentHead } from '@/lib/seo/useDocumentHead';
import {
  Alert,
  Button,
  Card,
  Chip,
  Container,
  EmptyState,
  Section,
  Skeleton,
} from '@/components/ui';
import { PostCard } from '@/components/content/PostCard';
import { RichTextBody } from '@/components/content/RichTextBody';
import { CtaBand } from '@/components/marketing/sections';
import { HERO_IMAGE_SIZES, mediaSrcSet, mediaUrl } from '@/lib/media';
import { formatDate } from '@/lib/format';
import { NotFoundPage } from './NotFoundPage';
import { PageHero } from '@/components/marketing/PageHero';

/**
 * The blog index.
 *
 * Filter and page state lives in the URL rather than in component state, so a filtered
 * view is linkable, survives a refresh, and works with the browser's back button. That
 * is the behaviour people expect from a website, and it is also what lets a category
 * page be shared or bookmarked.
 */
export function BlogIndexPage(): ReactNode {
  const { settings } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const category = searchParams.get('category') ?? undefined;
  const search = searchParams.get('q') ?? undefined;

  const query = useMemo(
    () => ({ page, limit: 9, ...(category ? { category } : {}), ...(search ? { q: search } : {}) }),
    [page, category, search],
  );

  const { data, isLoading, isError } = usePublicPosts(query);
  const { data: categories } = usePublicCategories();

  useDocumentHead({
    title: `Insights | ${settings.brand.name}`,
    description: 'Notes on hiring, talent markets and building teams that stay.',
    canonicalPath: '/blog',
  });

  const setParam = (key: string, value: string | undefined): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Changing a filter must reset pagination, or you land on page 3 of a
    // one-page result set and see nothing.
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Notes from the work"
        highlight="from the work"
        description="What we learn running searches across the UAE, India and beyond — written down so it is useful to somebody other than us."
      />

      <Section tone="surface" size="compact" className="pt-10 md:pt-14">
        <Container className="flex flex-col gap-10">
          {categories && categories.length > 0 ? (
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label="Filter by category"
            >
              <Button
                variant={category ? 'outline' : 'primary'}
                size="sm"
                onClick={() => setParam('category', undefined)}
                // Communicates the active filter to assistive technology, which
                // colour alone does not.
                aria-pressed={!category}
              >
                All
              </Button>
              {categories.map((item) => (
                <Button
                  key={item.id}
                  variant={category === item.slug ? 'primary' : 'outline'}
                  size="sm"
                  aria-pressed={category === item.slug}
                  onClick={() => setParam('category', item.slug)}
                >
                  {item.name}
                  <span className="ml-1 opacity-60">{item.postCount}</span>
                </Button>
              ))}
            </div>
          ) : null}

          {isError ? (
            <Alert tone="danger" title="Could not load insights">
              Something went wrong fetching the articles. Please refresh and try again.
            </Alert>
          ) : null}

          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <Card key={index} padding="compact" className="flex flex-col gap-4">
                  <Skeleton className="h-40 w-full rounded-input" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-full" />
                </Card>
              ))}
            </div>
          ) : data && data.items.length > 0 ? (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {data.items.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>

              {data.meta.totalPages > 1 ? (
                <nav
                  aria-label="Pagination"
                  className="flex items-center justify-center gap-3 pt-4"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.meta.hasPreviousPage}
                    onClick={() => setParam('page', String(page - 1))}
                  >
                    Previous
                  </Button>
                  {/* `aria-live` so a screen reader announces the new position after
                      the page changes, rather than leaving the user unsure. */}
                  <span aria-live="polite" className="text-sm text-muted">
                    Page {data.meta.page} of {data.meta.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!data.meta.hasNextPage}
                    onClick={() => setParam('page', String(page + 1))}
                  >
                    Next
                  </Button>
                </nav>
              ) : null}
            </>
          ) : (
            <EmptyState
              title={category || search ? 'Nothing matches that' : 'No insights published yet'}
              description={
                category || search
                  ? 'Try clearing the filter to see everything we have written.'
                  : 'Articles will appear here once the team publishes the first one.'
              }
              action={
                category || search ? (
                  <Button
                    variant="secondary"
                    onClick={() => setSearchParams(new URLSearchParams())}
                  >
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          )}
        </Container>
      </Section>
    </>
  );
}

// -----------------------------------------------------------------------------
// Article
// -----------------------------------------------------------------------------

export function BlogPostPage(): ReactNode {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  // A preview token in the URL lets a reviewer without an account read a draft.
  const previewToken = searchParams.get('preview') ?? undefined;

  const { data: post, isLoading, isError } = usePublicPost(slug ?? '', previewToken);

  useDocumentHead({
    title: post?.seo.title ?? 'Loading…',
    description: post?.seo.description,
    canonicalPath: slug ? `/blog/${slug}` : undefined,
    ogImageUrl: post?.coverMediaId ? mediaUrl(post.coverMediaId, 'large') : null,
    ogType: 'article',
    // Never let a previewed draft be indexed.
    noIndex: Boolean(previewToken) || post?.noIndex,
  });

  if (isLoading) {
    return (
      <Section className="pt-32 md:pt-40">
        <Container className="flex max-w-3xl flex-col gap-6">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full rounded-card" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </Container>
      </Section>
    );
  }

  // A missing or unpublished article renders the real 404 page, which the SPA host has
  // already returned a 404 status for.
  if (isError || !post) return <NotFoundPage />;

  return (
    <>
      <article>
        <Section tone="tint" size="hero">
          <Container className="flex max-w-3xl flex-col gap-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-strong hover:underline"
            >
              <ArrowLeft className="size-4" />
              All insights
            </Link>

            {previewToken ? (
              <Alert tone="warning" title="Preview">
                You are viewing an unpublished draft through a preview link. It is not visible to
                the public and is excluded from search engines.
              </Alert>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              {post.category ? <Chip>{post.category.name}</Chip> : null}
              <span className="text-sm text-muted">{post.readingMinutes} min read</span>
            </div>

            <h1 className="text-[2.25rem] font-semibold leading-[1.15] tracking-[-0.02em] text-heading md:text-[3rem]">
              {post.title}
            </h1>

            {post.excerpt ? <p className="text-body-lg text-body">{post.excerpt}</p> : null}

            <div className="flex items-center gap-3 text-sm text-muted">
              {post.author ? (
                <span className="font-medium text-body">{post.author.name}</span>
              ) : null}
              {post.publishedAt ? (
                <>
                  <span aria-hidden="true">·</span>
                  <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                </>
              ) : null}
            </div>
          </Container>
        </Section>

        {post.coverMediaId ? (
          <Container className="max-w-4xl">
            <img
              src={mediaUrl(post.coverMediaId, 'large')}
              srcSet={mediaSrcSet(post.coverMediaId)}
              sizes={HERO_IMAGE_SIZES}
              alt=""
              className="aspect-[16/9] w-full rounded-card object-cover shadow-soft"
            />
          </Container>
        ) : null}

        <Section tone="surface" size="compact" className="pt-10 md:pt-14">
          <Container className="max-w-3xl">
            <RichTextBody html={post.bodyHtml} />

            {post.tags.length > 0 ? (
              <div className="mt-12 flex flex-wrap items-center gap-2 border-t border-outline pt-8">
                <span className="text-sm font-semibold text-heading">Tagged</span>
                {post.tags.map((tag) => (
                  <Link key={tag.id} to={`/blog?tag=${tag.slug}`}>
                    <Chip tone="neutral">{tag.name}</Chip>
                  </Link>
                ))}
              </div>
            ) : null}
          </Container>
        </Section>
      </article>

      <CtaBand />
    </>
  );
}
